import React from 'react'

/**
 * Круговой индикатор заполнения контекстного окна.
 * Похож на индикатор Claude Code в footer-е чата: тонкий тёмный круг-фон +
 * заполнение по дуге = % использования.
 *
 * Цвета порогов:
 *   0-69%   — нейтральный акцент (var(--ctx-ring-base))
 *   70-89%  — янтарный warning
 *   90-100% — красный (контекст переполнен, история отрезается)
 *
 * Передавать percent можно undefined — тогда рисуется только пустой круг
 * (loading state).
 */
interface ContextUsageRingProps {
	percent?: number
	size?: number
	strokeWidth?: number
	className?: string
	'aria-label'?: string
}

export default function ContextUsageRing({
	percent,
	size = 14,
	strokeWidth = 2,
	className,
	'aria-label': ariaLabel,
}: ContextUsageRingProps) {
	// SVG-радиус берём с запасом под обводку, чтобы окружность не обрезалась.
	const r = (size - strokeWidth) / 2
	const cx = size / 2
	const cy = size / 2
	const circumference = 2 * Math.PI * r

	const clamped = typeof percent === 'number'
		? Math.max(0, Math.min(100, percent))
		: 0
	const dashOffset = circumference * (1 - clamped / 100)

	// Цветовой статус используем как class-modifier, чтобы стили жили
	// в SCSS и могли подхватывать тему / dark mode.
	let level: 'idle' | 'low' | 'mid' | 'high' = 'idle'
	if (typeof percent === 'number') {
		if (clamped >= 90) level = 'high'
		else if (clamped >= 70) level = 'mid'
		else level = 'low'
	}

	return (
		<svg
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			className={`ctx-ring ctx-ring--${level} ${className || ''}`.trim()}
			role={ariaLabel ? 'img' : undefined}
			aria-label={ariaLabel}
			aria-hidden={ariaLabel ? undefined : true}
		>
			<circle
				className="ctx-ring__track"
				cx={cx}
				cy={cy}
				r={r}
				fill="none"
				strokeWidth={strokeWidth}
			/>
			{typeof percent === 'number' && (
				<circle
					className="ctx-ring__fill"
					cx={cx}
					cy={cy}
					r={r}
					fill="none"
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={dashOffset}
					// Поворачиваем чтобы старт дуги был сверху (12 часов).
					transform={`rotate(-90 ${cx} ${cy})`}
				/>
			)}
		</svg>
	)
}
